import fileHelper from 'app/helpers/fileHelper';
import { Product } from 'app/shared/model/ProductBase';
import React, { useEffect, useState } from 'react';
import { Button, Tooltip, Space, Tag, notification } from 'antd';
import {
  FilterOutlined,
  SaveOutlined,
  EditOutlined,
  DownloadOutlined,
  PlusOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { SaveState } from 'app/shared/ui/GenericSaveAndBackButton';
import { Fee } from 'app/shared/model/Fee';
import { CancellationFee } from 'app/shared/model/CancellationFee';

interface TableActionButtonsProps {
  onShowFiltersClick?: (event: React.SyntheticEvent) => void;
  showFilters?: boolean;
  onAddClick?: () => void;
  canFilter: boolean;
  canExport: boolean;
  canEdit: boolean;
  canAdd: boolean;
  editMode?: boolean;
  onEditClick?: (event: React.SyntheticEvent) => void;
  onEditCancel?: () => void;
  onEditSave?: () => Promise<{ ok: boolean; message?: string } | void> | { ok: boolean; message?: string } | void;
  isSaving?: boolean;
  dataList?: Product[] | Fee[] | CancellationFee[] | string;
  csvFileName?: string;
  formState?: SaveState;
  validationHint?: string;
}

const TableActionButtons = (props: TableActionButtonsProps) => {
  const [labelActive, setLabelActive] = useState(true);

  useEffect(() => {
    if (labelActive) {
      const timer = setTimeout(() => setLabelActive(false), 30000);
      return () => clearTimeout(timer);
    }
  }, [labelActive]);

  const exportToCSVHandler = () => {
    if (props.dataList && props.csvFileName) {
      typeof props.dataList === 'string'
        ? fileHelper.downloadCSV(props.dataList, props.csvFileName)
        : fileHelper.generateCSV(props.dataList as any, props.csvFileName);
    }
  };

  const handleSaveClick = async () => {
    if (!props.onEditSave) {
      return;
    }

    try {
      const response = await props.onEditSave();
      if (!response || typeof response !== 'object' || !('ok' in response)) {
        return;
      }
      const result = response as { ok: boolean; message?: string; errors?: string[] };
      if (result.ok) {
        const messageText = result.message || 'Changes saved successfully.';
        notification.success({ message: 'Success', description: messageText });
      } else {
        const errorText =
          result.errors && result.errors.length > 0
            ? result.errors.join('\n')
            : result.message || 'Failed to save changes.';
        notification.error({
          message: 'Error',
          description: (
            <div style={{ whiteSpace: 'pre-line' }}>{errorText}</div>
          ),
        });
      }
    } catch (error: any) {
      const description = error?.message ?? String(error);
      notification.error({ message: 'Error', description });
    }
  };

  const renderLabel = () => {
    switch (props.formState) {
      case SaveState.NoChanges:
        return <Tag color="blue">No changes detected</Tag>;
      case SaveState.PendingChanges:
        return <Tag color="orange">Unsaved changes detected</Tag>;
      case SaveState.SaveError:
        return <Tag color="red">Error while saving!</Tag>;
      case SaveState.ValidationError:
        return (<Tag color="yellow">{props.validationHint ?? "Please check highlighted fields"}</Tag>);
      case SaveState.Saving:
        return <Tag color="orange">Saving...</Tag>;
      case SaveState.Saved:
        return labelActive ? <Tag color="green">Changes saved successfully!</Tag> : null;
      default:
        return null;
    }
  };

  return (
    <Space>
      {props.canFilter && props.onShowFiltersClick && (
        <Tooltip title={`${props.showFilters ? 'Hide' : 'Show'} filters`}>
          <Button icon={<FilterOutlined />} type="primary" onClick={props.onShowFiltersClick} />
        </Tooltip>
      )}

      {(props.canEdit || props.editMode) && (
        <Space>
          {props.editMode ? (
            <>
              {renderLabel()}
              <Button
                icon={<SaveOutlined />}
                disabled={props.formState === SaveState.NoChanges}
                loading={props.isSaving}
                type="primary"
                onClick={handleSaveClick}
              >
                Save
              </Button>
              <Button icon={<CloseCircleOutlined />} onClick={props.onEditCancel}>
                Cancel
              </Button>
              {props.canAdd && props.onAddClick && (
                <Button
                  icon={<PlusOutlined />}
                  onClick={props.onAddClick}
                >
                  Add
                </Button>
              )}
            </>
          ) : (
            <>
              {renderLabel()}
              <Button icon={<EditOutlined />} type="primary" onClick={props.onEditClick}>
                Edit
              </Button>
            </>
          )}
        </Space>
      )}

      {props.canExport && !props.editMode && (
        <Button icon={<DownloadOutlined />} onClick={exportToCSVHandler}>
          Export CSV
        </Button>
      )}
    </Space>
  );
};

export default TableActionButtons;